import { html, css } from "lit";
import { customElement } from "lit/decorators.js";
import { StyleInfo, styleMap } from "lit/directives/style-map.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { dom } from "@omegagrid/core";
import { BaseVisualization, ReportEvent } from "./baseVisualization";
import { ContextData, TextViewOptions } from "@reporting/rp-types/analytics";
import { openReport } from "../../actions";

const formatColor = (color: string) => /^[0-9A-Fa-f]{6}$/.test(color) ? `#${color}` : color;

/**
 * Simple text item without any connection to the data model.
 * Content and formatting are stored in context view_options,
 * edited via TextVisualizationEditor (as a dashboard subeditor).
 */
@customElement('rp-visual-text')
export class TextVisualization extends BaseVisualization<ContextData> {

	static styles = css`
		* {
			box-sizing: border-box;
		}

		:host {
			display: block;
			width: 100%;
			height: 100%;
			position: relative;
			overflow: auto;
		}

		.container {
			display: flex;
			flex-direction: column;
			width: 100%;
			height: 100%;
		}

		.content {
			padding: 5px;
			white-space: pre-wrap;
			width: 100%;
		}

		.container.link {
			cursor: pointer;
		}

		.container.link:hover .text {
			text-decoration: underline;
		}
	`;

	get viewOptions(): TextViewOptions {
		return (this.contextData?.view_options ?? {}) as TextViewOptions;
	}

	get containerStyle(): StyleInfo {
		const vertical = this.viewOptions.style?.alignment?.vertical || 'center';
		return {
			justifyContent: {top: 'flex-start', center: 'center', bottom: 'flex-end'}[vertical],
		};
	}

	get textStyle(): StyleInfo {
		const style = this.viewOptions.style ?? {};
		const alignment = style.alignment ?? {};
		const decorations = [
			style.underline ? 'underline' : null,
			style.strike ? 'line-through' : null,
		].filter(Boolean);

		return {
			fontSize: style.sz ? `${style.sz}px` : null,
			fontFamily: style.font || null,
			color: style.color ? formatColor(style.color) : null,
			textAlign: alignment.horizontal || 'center',
			fontWeight: style.bold ? 'bold' : null,
			fontStyle: style.italic ? 'italic' : null,
			textDecoration: decorations.length ? decorations.join(' ') : null,
			// html content handles its own whitespace/layout
			whiteSpace: this.viewOptions.html ? 'normal' : (alignment.wrapText === false ? 'pre' : null),
		};
	}

	refresh(contextData?: ContextData) {
		super.refresh(contextData);
		this.requestUpdate();
	}

	firstUpdated() {
		this.dispatchEvent(new ReportEvent('load', {visualization: this}));
	}

	updated() {
		// custom css applied in html mode
		const cssStyle = this.viewOptions.html ? this.viewOptions.css_style : null;
		if (cssStyle) {
			dom.appendStyle(this.shadowRoot, cssStyle, '__style');
		} else {
			this.shadowRoot.querySelector('#__style')?.remove();
		}
	}

	openLink() {
		const idContext = this.viewOptions.id_context;
		if (!idContext) return;
		openReport(idContext, {target: 'new_tab'});
	}

	render = () => html`
		<div
			class="container${this.viewOptions.id_context ? ' link' : ''}"
			style="${styleMap(this.containerStyle)}"
			@click="${() => this.openLink()}">
			<div class="content" style="${styleMap(this.textStyle)}">${this.viewOptions.html
				? unsafeHTML(this.viewOptions.content || '')
				: (this.viewOptions.content || '')
			}</div>
		</div>
	`;

	layout() { }

}
